import { useApolloClient } from '@apollo/client'
import { FirebaseError } from '@firebase/util'
import axios, { AxiosError } from 'axios'
import { getAuth } from 'firebase/auth'
import fileDownload from 'js-file-download'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { v4 } from 'uuid'
import config from '../../../../../config'
import { GetDirectLinksDocument } from '../../../../../graphql/generated'
import useAlerts from '../../../hooks/useAlerts'

interface ImageDownloadProps {
  albumIds?: (string | null | undefined)[] | null
  imageIds?: (string | null | undefined)[] | null
}

interface SingleImageDownloadProps {
  url: string
  originalFilename: string
  title: string
  id: string
  isPublic?: boolean
}

const findNameWithExtension = (title: string, originalFilename: string): string => {
  // fall back to original file name if there is no title
  if (isEmpty(title)) {
    return originalFilename
  }

  const originalExtension = originalFilename.split('.').pop()?.toLowerCase()
  return `${title}.${originalExtension}`
}

export default function useImageDownload() {
  const apolloClient = useApolloClient()
  const { createAlert } = useAlerts()

  const downloadMultiple = async (props: ImageDownloadProps) => {
    try {
      createAlert('Media is downloading...', 'info')
      const authorization: string | undefined = await getAuth().currentUser?.getIdToken()
      if (!authorization) {
        throw new FirebaseError('auth/session-expired', 'Image download failed. Session expired.')
      }
      const headers = { Authorization: authorization, 'X-Correlation-Id': v4() }
      const requestBody = { ...props }
      const response = await axios.post(config.api.download, requestBody, { responseType: 'blob', headers })
      const blob = new Blob([response.data], { type: 'application/zip' })
      const fileName = ['archive', '-', moment().valueOf(), '.', 'zip'].join('')
      fileDownload(blob, fileName, 'application/zip')
      createAlert('Your Media has successfully downloaded.', 'success')
    } catch (error) {
      const err = error as AxiosError<Blob>
      let message = 'There was an error while trying to download your media.'
      if (err.response?.data) {
        message = await err.response.data.text()
      }
      createAlert(message)
    }
  }

  // used by public sharing pages in new architecture to support legacy links
  const download = async ({ url, originalFilename, title, id, isPublic = false }: SingleImageDownloadProps) => {
    try {
      createAlert('Media is downloading...', 'info')
      const imageName = findNameWithExtension(title, originalFilename) || originalFilename
      let link
      if (isPublic) {
        link = url
      } else {
        try {
          const imageDirectLinkResponse = await apolloClient.query({
            query: GetDirectLinksDocument,
            variables: { ids: [id] }
          })

          link = imageDirectLinkResponse.data.getDirectLinks[0] || url
        } catch (error) {
          createAlert('There was a problem while trying to generate direct links.')
        }
      }

      if (link) {
        const response = await axios.get(link, {
          responseType: 'blob'
        })
        fileDownload(response.data, imageName)
        createAlert('Your media has successfully downloaded.', 'success')
      }
    } catch (error) {
      const err = error as AxiosError<Blob>
      let message = 'There was an error while trying to download your media.'
      if (err.response?.data) {
        message = await err.response.data.text()
      }
      createAlert(message)
    }
  }

  return { download, downloadMultiple, findNameWithExtension }
}
